// Booking.js - Customer booking form
// Uses new Google Places Autocomplete API (post March 2025)
// Auto-fills city and state when customer selects a cemetery
// Progress indicator shows customer where they are in the booking process
// Gravesite autofill when coming from saved gravesite on profile

import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';

// Cities served — Mahoning County OH, Trumbull County OH, and Lawrence County PA
const SERVED_CITIES = [
  // Mahoning County, Ohio
  'youngstown', 'boardman', 'canfield', 'poland', 'struthers', 'campbell',
  'sebring', 'alliance', 'austintown', 'beaver', 'beloit', 'berlin center',
  'columbiana', 'craig beach', 'ellsworth', 'goshen', 'jackson', 'lake milton',
  'lowellville', 'new middletown', 'north lima', 'springfield',
  // Trumbull County, Ohio
  'warren', 'niles', 'cortland', 'girard', 'hubbard', 'brookfield',
  'newton falls', 'lordstown', 'bazetta', 'bloomfield', 'bristolville',
  'champion', 'farmington', 'fowler', 'kinsman', 'liberty', 'leavittsburg',
  'mcdonald', 'masury', 'mineral ridge', 'orwell', 'southington', 'vienna',
  'weathersfield',
  // Lawrence County, Pennsylvania
  'new castle', 'ellwood city', 'wampum', 'wurtemburg', 'volant',
  'enon valley', 'edinburg', 'bessemer', 'north sewickley', 'pulaski',
  'union township', 'neshannock', 'scott township', 'shenango',
  'hickory', 'princeton', 'wayne township', 'plain grove',
];

// Add-on options shown after gravesite info — each toggled independently
const ADD_ONS = [
  // Flowers
  { id: 'bouquet',         label: 'Fresh Cut Bouquet',          description: 'Fresh cut flowers placed beautifully at the site',                   price: 20, category: 'Flowers' },
  { id: 'planted_flowers', label: 'Planted Seasonal Flowers',   description: 'Seasonal flowers planted at the grave site',                         price: 35, category: 'Flowers' },
  { id: 'flowers_refresh', label: 'Flower Bed Refresh',         description: 'Fresh mulch, light bed grooming, and weed touch-up',                 price: 15, category: 'Flowers' },
  { id: 'flowers_bundle',  label: 'Flowers + Refresh Bundle',   description: 'Planted seasonal flowers plus full bed refresh — save $10',          price: 40, category: 'Flowers' },
  // Decorations
  { id: 'seasonal_decor',  label: 'Seasonal Decoration',        description: 'Wreath, yard stakes, or seasonal display',                           price: 20, category: 'Decorations' },
  { id: 'birthday',        label: 'Birthday Remembrance Setup', description: 'Balloon stakes and flowers for a birthday tribute',                  price: 20, category: 'Decorations' },
  { id: 'bow',             label: 'Bow / Ribbon',               description: 'Decorative bow or ribbon placed at the site',                        price: 10, category: 'Decorations' },
  { id: 'solar_light',     label: 'Solar Memorial Light',       description: 'Solar light installed at the grave site',                            price: 18, category: 'Decorations' },
  // Flags
  { id: 'flag_small',      label: 'Veteran / Holiday Flag',     description: 'Small flag placed with honor at the site',                           price: 8,  category: 'Flags' },
  { id: 'flag_full',       label: 'Full Flag Kit',              description: 'Full flag display kit placed at the site',                           price: 18, category: 'Flags' },
  // Care
  { id: 'overgrowth',      label: 'Heavy Overgrowth Clearing',  description: 'Removal of heavy overgrowth around the grave site',                  price: 25, category: 'Care' },
  { id: 'extra_grave',     label: 'Additional Grave',           description: 'Full service cleaning for one additional grave at the same cemetery', price: 30, category: 'Care' },
  { id: 'extended',        label: 'Extended Visit',             description: 'Extra 30 minutes of dedicated care',                                 price: 15, category: 'Care' },
  // Media
  { id: 'video',           label: 'Video Update',               description: 'A short video of the completed visit sent to you',                   price: 15, category: 'Media' },
  { id: 'report',          label: 'Written Visit Report',       description: 'Detailed written report of the visit',                               price: 10, category: 'Media' },
];

const BASE_PRICE = 59;

function Booking() {
  const navigate = useNavigate();
  const location = useLocation();

  // Form field state
  const [lovedOneName, setLovedOneName] = useState('');
  const [cemeteryName, setCemeteryName] = useState('');
  const [cemeteryCity, setCemeteryCity] = useState('');
  const [cemeteryState, setCemeteryState] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [preferredDate, setPreferredDate] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoFilledFrom, setAutoFilledFrom] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [graveSection, setGraveSection] = useState('');

  // Waitlist modal state
  const [showWaitlist, setShowWaitlist] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [waitlistState, setWaitlistState] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  // Ref for the cemetery input element
  // We attach Google Places autocomplete directly to this input
  const cemeteryInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Toggle an add-on in or out of the selection
  const toggleAddOn = (addOnId) => {
    setSelectedAddOns(prev =>
      prev.includes(addOnId)
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  // Running total in dollars — $59 base plus sum of selected add-on prices
  const totalPrice = BASE_PRICE + selectedAddOns.reduce((sum, id) => {
    const addOn = ADD_ONS.find(a => a.id === id);
    return sum + (addOn ? addOn.price : 0);
  }, 0);

  // Load Google Places script and attach autocomplete to cemetery input
  useEffect(() => {
    const loadGooglePlaces = () => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
        return;
      }

      // Check if script is already loading
      const existingScript = document.getElementById('google-places-script');
      if (existingScript) {
        existingScript.onload = initAutocomplete;
        return;
      }

      // Create and inject the script
      const script = document.createElement('script');
      script.id = 'google-places-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    };

    // Initialize Google Places Autocomplete on the cemetery input
    const initAutocomplete = () => {
      if (!cemeteryInputRef.current || !window.google) return;

      // Bounding box covering all three service counties:
      // Mahoning County OH, Trumbull County OH, and Lawrence County PA.
      // Expanded west to -81.10 to include Lawrence County PA.
      const serviceAreaBounds = new window.google.maps.LatLngBounds(
        { lat: 40.85, lng: -81.10 }, // SW corner (southern Lawrence County PA)
        { lat: 41.52, lng: -80.35 }  // NE corner (northern Trumbull County OH)
      );

      // 'establishment' is the correct type for Places Autocomplete — 'cemetery'
      // is not a valid Autocomplete type and causes error icons to appear in the input
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        cemeteryInputRef.current,
        {
          types: ['establishment'],
          bounds: serviceAreaBounds,
          strictBounds: true,
          fields: ['name', 'address_components'],
        }
      );

      // Listen for when customer selects a place from the dropdown
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.address_components) return;

        // Set cemetery name
        setCemeteryName(place.name || '');

        // Extract city and state from address components
        let city = '';
        let state = '';

        place.address_components.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.short_name;
          }
        });

        setCemeteryCity(city);
        const stateLower = state.toLowerCase();
        setCemeteryState(stateLower);

        // Show waitlist if the selected cemetery is outside our service area
        const cityMatch = SERVED_CITIES.includes(city.toLowerCase().trim());
        if (!cityMatch) {
          setWaitlistSubmitted(false);
          setWaitlistEmail('');
          setShowWaitlist(true);
        }

        // Update the input value to show the cemetery name
        if (cemeteryInputRef.current) {
          cemeteryInputRef.current.value = place.name || '';
        }
      });
    };

    loadGooglePlaces();

    // Cleanup autocomplete listener on unmount
    return () => {
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Check if customer came from a saved gravesite and auto-fill fields
  useEffect(() => {
    if (location.state?.gravesite) {
      const g = location.state.gravesite;
      setLovedOneName(g.lovedOneName || '');
      setCemeteryName(g.cemeteryName || '');
      setCemeteryCity(g.cemeteryCity || '');
      setCemeteryState(g.cemeteryState || '');
      setSpecialInstructions(g.notes || '');
      setAutoFilledFrom(g.lovedOneName || '');

      // Also set the input ref value for the cemetery field
      if (cemeteryInputRef.current) {
        cemeteryInputRef.current.value = g.cemeteryName || '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save waitlist entry to Firestore and close modal
  const handleWaitlistSubmit = async () => {
    if (!waitlistEmail) return;

    // Validate email format and length before writing to Firestore
    // Prevents garbage data and oversized strings hitting the database
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmed = waitlistEmail.trim();
    if (!emailRegex.test(trimmed) || trimmed.length > 254) {
      setWaitlistError('Please enter a valid email address.');
      return;
    }

    setWaitlistError('');
    setWaitlistLoading(true);
    try {
      await addDoc(collection(db, 'waitlist'), {
        email: trimmed.toLowerCase(),
        state: waitlistState,
        type: 'customer',
        createdAt: new Date(),
      });
      setWaitlistSubmitted(true);
    } catch (err) {
      console.error('Waitlist save error:', err.code || 'unknown');
      setWaitlistError('Something went wrong. Please try again.');
    }
    setWaitlistLoading(false);
  };

  // Helper to capitalize words (used in summary card)
  const capitalize = (str) => {
    if (!str) return '';
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Phase 1 — validate fields and show summary card (no Firestore write yet)
  const handleReview = () => {
    const finalCemeteryName = cemeteryInputRef.current?.value || cemeteryName;
    if (!lovedOneName || !finalCemeteryName || !cemeteryCity || !cemeteryState || !preferredDate) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    setShowSummary(true);
  };

  // Phase 2 — write to Firestore and navigate to payment
  const handleConfirm = async () => {
    const finalCemeteryName = cemeteryInputRef.current?.value || cemeteryName;

    const user = auth.currentUser;
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Expand selected add-on IDs into full objects for storage
      const addOnDetails = selectedAddOns.map(id => {
        const addOn = ADD_ONS.find(a => a.id === id);
        return { id, label: addOn.label, price: addOn.price };
      });

      const bookingRef = await addDoc(collection(db, 'bookings'), {
        customerId: user.uid,
        customerEmail: user.email,
        lovedOneName: lovedOneName,
        cemeteryName: finalCemeteryName,
        cemeteryCity: cemeteryCity.toLowerCase().trim(),
        cemeteryState: cemeteryState.toLowerCase().trim(),
        basePrice: BASE_PRICE,
        addOns: addOnDetails,
        preferredDate: preferredDate,
        specialInstructions: specialInstructions,
        graveSection: graveSection.trim(),
        status: 'pending_payment',
        createdAt: new Date(),
        photos: [],
      });

      // Server computes the amount from the booking — only pass bookingId
      navigate('/payment', {
        state: {
          bookingId: bookingRef.id
        }
      });

    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Booking error:', err);
      setLoading(false);
    }
  };

  return (
    <div className="booking-page">

      {/* Logo */}
      <a href="/dashboard" className="auth-logo">Evermark Memorial</a>

      {/* ========== PROGRESS INDICATOR ========== */}
      <div className="progress-bar">
        <div className="progress-step progress-active">
          <div className="progress-circle">1</div>
          <span className="progress-label">Details</span>
        </div>
        <div className="progress-line"></div>
        <div className="progress-step progress-upcoming">
          <div className="progress-circle">2</div>
          <span className="progress-label">Payment</span>
        </div>
        <div className="progress-line"></div>
        <div className="progress-step progress-upcoming">
          <div className="progress-circle">3</div>
          <span className="progress-label">Confirmed</span>
        </div>
      </div>

      {/* ========== BOOKING FORM CARD ========== */}
      <div className="booking-card-form">
        <h2>Book a Grave Visit</h2>
        <p className="auth-subtitle">
          Fill in the details below and we'll find a trusted local caretaker
        </p>

        {/* Auto-fill notice */}
        {autoFilledFrom && (
          <div className="autofill-notice">
            ✨ Pre-filled from your saved gravesite for {autoFilledFrom}
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}

        {/* ========== LOVED ONE'S NAME ========== */}
        <div className="auth-field">
          <label>Loved One's Full Name <span className="required">*</span></label>
          <input
            type="text"
            placeholder="e.g. John Michael Smith"
            value={lovedOneName}
            onChange={(e) => setLovedOneName(e.target.value)}
            maxLength={150}
          />
        </div>

        {/* ========== CEMETERY NAME WITH GOOGLE AUTOCOMPLETE ==========
            Uses a ref so Google Places can attach directly to the input
            Selecting a cemetery auto-fills name, city, and state */}
        <div className="auth-field">
          <label>Cemetery Name <span className="required">*</span></label>
          <input
            ref={cemeteryInputRef}
            type="text"
            placeholder="Start typing a cemetery name..."
            defaultValue={cemeteryName}
            className="auth-input-autocomplete"
          />
          <p className="autocomplete-hint">
            Start typing and select your cemetery from the dropdown
          </p>
        </div>

        {/* ========== CITY AND STATE ==========
            Auto-filled when customer selects a cemetery
            Can still be edited manually if needed */}
        <div className="auth-row">
          <div className="auth-field">
            <label>City <span className="required">*</span></label>
            <input
              type="text"
              placeholder="Auto-filled from cemetery"
              value={cemeteryCity}
              onChange={(e) => setCemeteryCity(e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="auth-field">
            <label>State <span className="required">*</span></label>
            <select
              className="booking-select"
              value={cemeteryState}
              onChange={(e) => {
                const val = e.target.value;
                setCemeteryState(val);
                // OH and PA are both served — only show waitlist for other states
              if (val && val !== 'oh' && val !== 'pa') {
                  setWaitlistSubmitted(false);
                  setWaitlistEmail('');
                  setShowWaitlist(true);
                }
              }}
            >
              <option value="">Select state</option>
              <option value="al">Alabama</option>
              <option value="ak">Alaska</option>
              <option value="az">Arizona</option>
              <option value="ar">Arkansas</option>
              <option value="ca">California</option>
              <option value="co">Colorado</option>
              <option value="ct">Connecticut</option>
              <option value="de">Delaware</option>
              <option value="fl">Florida</option>
              <option value="ga">Georgia</option>
              <option value="hi">Hawaii</option>
              <option value="id">Idaho</option>
              <option value="il">Illinois</option>
              <option value="in">Indiana</option>
              <option value="ia">Iowa</option>
              <option value="ks">Kansas</option>
              <option value="ky">Kentucky</option>
              <option value="la">Louisiana</option>
              <option value="me">Maine</option>
              <option value="md">Maryland</option>
              <option value="ma">Massachusetts</option>
              <option value="mi">Michigan</option>
              <option value="mn">Minnesota</option>
              <option value="ms">Mississippi</option>
              <option value="mo">Missouri</option>
              <option value="mt">Montana</option>
              <option value="ne">Nebraska</option>
              <option value="nv">Nevada</option>
              <option value="nh">New Hampshire</option>
              <option value="nj">New Jersey</option>
              <option value="nm">New Mexico</option>
              <option value="ny">New York</option>
              <option value="nc">North Carolina</option>
              <option value="nd">North Dakota</option>
              <option value="oh">Ohio</option>
              <option value="ok">Oklahoma</option>
              <option value="or">Oregon</option>
              <option value="pa">Pennsylvania</option>
              <option value="ri">Rhode Island</option>
              <option value="sc">South Carolina</option>
              <option value="sd">South Dakota</option>
              <option value="tn">Tennessee</option>
              <option value="tx">Texas</option>
              <option value="ut">Utah</option>
              <option value="vt">Vermont</option>
              <option value="va">Virginia</option>
              <option value="wa">Washington</option>
              <option value="wv">West Virginia</option>
              <option value="wi">Wisconsin</option>
              <option value="wy">Wyoming</option>
            </select>
          </div>
        </div>

        {/* ========== SECTION / ROW ==========
            Optional — helps caretaker find the grave quickly */}
        <div className="auth-field">
          <label>
            Section / Row / Plot
            <span className="optional"> (optional but recommended)</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Section B, Row 12, Plot 4"
            value={graveSection}
            onChange={(e) => setGraveSection(e.target.value)}
            maxLength={150}
          />
          <p className="autocomplete-hint">
            Found on the cemetery office records or the deed — helps your caretaker locate the grave
          </p>
        </div>

        {/* ========== BASE PRICE DISPLAY ========== */}
        <div className="base-price-display">
          <div className="base-price-top">
            <span className="base-price-label">Base Visit</span>
            <span className="base-price-amount">$59</span>
          </div>
          <span className="base-price-desc">Headstone cleaning, respectful care, peace of mind</span>
        </div>

        {/* ========== ADD-ONS — grouped by category ========== */}
        <div className="auth-field">
          <label>Make It More Meaningful <span className="optional">(optional)</span></label>
          <p className="autocomplete-hint">Select any add-ons to include with your visit</p>
          {['Flowers', 'Decorations', 'Flags', 'Care', 'Media'].map(category => (
            <div key={category} className="addon-category-group">
              <div className="addon-category-header">
                <span className="addon-category-title">{category}</span>
                {/* Flowers and Decorations involve physical materials the caretaker buys */}
                {(category === 'Flowers' || category === 'Decorations') && (
                  <span className="addon-materials-note">Caretaker purchases materials — receipt photo required</span>
                )}
              </div>
              <div className="addon-cards">
                {ADD_ONS.filter(a => a.category === category).map(addOn => (
                  <div
                    key={addOn.id}
                    className={`addon-card ${selectedAddOns.includes(addOn.id) ? 'addon-selected' : ''}`}
                    onClick={() => toggleAddOn(addOn.id)}
                  >
                    <div className="addon-check">{selectedAddOns.includes(addOn.id) ? '✓' : '+'}</div>
                    <div className="addon-info">
                      <span className="addon-label">{addOn.label}</span>
                      <span className="addon-desc">{addOn.description}</span>
                    </div>
                    <span className="addon-price">+${addOn.price}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Running total — updates live as add-ons are toggled */}
        <div className="service-price-preview">
          {selectedAddOns.length === 0 ? (
            <>
              <span className="service-price-label">Total:</span>
              <span className="service-price-amount">$59</span>
            </>
          ) : (
            <>
              <span className="service-price-label">Total:</span>
              <span className="service-price-amount">${totalPrice}</span>
              <span className="price-breakdown">
                $59 + {selectedAddOns.map(id => {
                  const a = ADD_ONS.find(x => x.id === id);
                  return `$${a.price}`;
                }).join(' + ')} = $${totalPrice}
              </span>
            </>
          )}
        </div>

        {/* ========== PREFERRED DATE ========== */}
        <div className="auth-field">
          <label>Preferred Visit Date <span className="required">*</span></label>
          <input
            type="date"
            value={preferredDate}
            onChange={(e) => setPreferredDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* ========== SPECIAL INSTRUCTIONS ========== */}
        <div className="auth-field">
          <label>
            Special Instructions
            <span className="optional"> (optional)</span>
          </label>
          <textarea
            className="booking-textarea"
            placeholder="e.g. Please place white flowers only. The grave is in section B, row 12."
            value={specialInstructions}
            onChange={(e) => {
              if (e.target.value.length <= 500) setSpecialInstructions(e.target.value);
            }}
            rows={4}
            maxLength={500}
          />
          {/* Live character counter */}
          <div className="char-counter">
            <span className={specialInstructions.length >= 480 ? 'char-counter-warn' : ''}>
              {specialInstructions.length}/500
            </span>
          </div>
        </div>

        {/* Booking summary card — shown after Review Booking is clicked */}
        {showSummary && (
          <div className="booking-summary-card">
            <h3>Review Your Booking</h3>
            <div className="summary-row">
              <span className="summary-label">Loved one</span>
              <span className="summary-value">{lovedOneName}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Cemetery</span>
              <span className="summary-value">{capitalize(cemeteryInputRef.current?.value || cemeteryName)}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Location</span>
              <span className="summary-value">{capitalize(cemeteryCity)}, {cemeteryState.toUpperCase()}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Base visit</span>
              <span className="summary-value">Headstone Cleaning — $59</span>
            </div>
            {selectedAddOns.length > 0 && (
              <div className="summary-row">
                <span className="summary-label">Add-ons</span>
                <span className="summary-value">
                  {selectedAddOns.map(id => {
                    const a = ADD_ONS.find(x => x.id === id);
                    return `${a.label} ($${a.price})`;
                  }).join(', ')}
                </span>
              </div>
            )}
            <div className="summary-row">
              <span className="summary-label">Date</span>
              <span className="summary-value">{new Date(preferredDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}</span>
            </div>
            {graveSection && (
              <div className="summary-row">
                <span className="summary-label">Section/Row</span>
                <span className="summary-value">{graveSection}</span>
              </div>
            )}
            {specialInstructions && (
              <div className="summary-row">
                <span className="summary-label">Notes</span>
                <span className="summary-value">{specialInstructions}</span>
              </div>
            )}
            <div className="summary-price-row">
              <span className="summary-label">Total charge</span>
              <span className="summary-price">${totalPrice}</span>
            </div>
            <button className="btn-link" onClick={() => setShowSummary(false)}>← Edit details</button>
          </div>
        )}

        {/* Submit button — Review first, then Confirm */}
        <button
          className="btn-primary btn-full"
          onClick={showSummary ? handleConfirm : handleReview}
          disabled={loading}
        >
          {loading ? 'Saving details...' : showSummary ? 'Continue to Payment →' : 'Review Booking →'}
        </button>

        <p className="auth-switch">
          <a href="/dashboard">← Back to Dashboard</a>
        </p>

      </div>

      {/* ========== WAITLIST MODAL ==========
          Shown when customer selects a state we don't currently serve */}
      {showWaitlist && (
        <div className="waitlist-overlay" onClick={() => setShowWaitlist(false)}>
          <div className="waitlist-modal" onClick={(e) => e.stopPropagation()}>
            {!waitlistSubmitted ? (
              <>
                <div className="waitlist-icon">🌿</div>
                <h3>We're not in your area yet</h3>
                <p className="waitlist-body">
                  We are not yet serving your area. We are currently serving Mahoning County
                  and Trumbull County in Northeast Ohio, and Lawrence County in Western
                  Pennsylvania. Enter your email and we'll notify you when we expand to your area.
                </p>
                <div className="auth-field" style={{ textAlign: 'left', marginTop: '16px' }}>
                  <label>Your Email</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={waitlistEmail}
                    onChange={(e) => { setWaitlistEmail(e.target.value); setWaitlistError(''); }}
                    maxLength={254}
                  />
                  {/* Inline validation error for the waitlist email field */}
                  {waitlistError && <div className="auth-error" style={{ marginTop: '6px' }}>{waitlistError}</div>}
                </div>
                <button
                  className="btn-primary btn-full"
                  style={{ marginTop: '12px' }}
                  onClick={handleWaitlistSubmit}
                  disabled={waitlistLoading || !waitlistEmail}
                >
                  {waitlistLoading ? 'Saving...' : 'Notify Me When Available'}
                </button>
                <button
                  className="btn-link"
                  style={{ marginTop: '8px', display: 'block', width: '100%', textAlign: 'center' }}
                  onClick={() => setShowWaitlist(false)}
                >
                  Maybe Later
                </button>
              </>
            ) : (
              <>
                <div className="waitlist-icon">✓</div>
                <h3>You're on the list!</h3>
                <p className="waitlist-body">
                  We'll email you as soon as Evermark Memorial launches in your area. Thank you for your interest.
                </p>
                <button
                  className="btn-primary btn-full"
                  style={{ marginTop: '16px' }}
                  onClick={() => setShowWaitlist(false)}
                >
                  Got it
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default Booking;